import { getPriorityBarColors } from 'helpers/priorityColor.helper/priorityColor.helper'
import i18n from 'i18next'
import React, { CSSProperties, ReactElement, useMemo, useRef } from 'react'
import { colors } from 'styles/variables'
import Datetime from 'utils/datetime/datetime'
import { CalendarEvent } from 'views/EventsView/components/EventsCalendar/EventsCalendar'
import { FILLER_EVENT_ID, getVisibleEvents } from './BarCalendar.helper'
import styles from './BarCalendar.module.less'
import { Category, Event } from './BarCalendar.types'
import { BCCategoryName } from './components/BCCategoryName/BCCategoryName'
import Column from './components/BCColumn/BCColumn'
import EventBar from './components/BCEventBar/BCEventBar'
import Row from './components/BCRow/BCRow'
import { useCalendarScrollPosition } from './useCalendarScrollPosition'

type BarCalenderProps = {
  startTime: ISODateTime
  endTime: ISODateTime
  events: Event[] | CalendarEvent[]
  categories: Category[]
  onClick?: (setting: Event) => void
  /** Rings the bars it returns true for and tints their categories' rows. */
  isEventHighlighted?: (event: Event) => boolean
  header?: string
  /** Width of the category column in pixels; 240 when left out. */
  categoryColumnWidth?: number
  isOperationalEventCalendar?: boolean
  disableDefaultSorting?: boolean
  isCollectedSettings?: boolean
}

function formatEvents(
  events: (Event | CalendarEvent)[],
  startTime: string,
  endTime: string
): {
  timeRows: { [key: string]: { [key: string]: (Event | CalendarEvent)[][] } }
  totalRowsByCategory: { [key: string]: number }
} {
  events = events.filter((event) => {
    const eventStartTime = event.startTime ? new Date(event.startTime).getTime() : null
    const eventEndTime = event.endTime ? new Date(event.endTime).getTime() : null
    const visibleStartTime = new Date(startTime).getTime()
    const visibleEndTime = new Date(endTime).getTime()

    return (
      (eventEndTime == null || eventEndTime >= visibleStartTime) && eventStartTime && eventStartTime <= visibleEndTime
    )
  })

  const eventsByCategory: { [key: string]: (Event | CalendarEvent)[] } = {}
  events.forEach((event) => {
    if (!eventsByCategory[event.categoryId]) {
      eventsByCategory[event.categoryId] = []
    }
    eventsByCategory[event.categoryId].push(event)
  })

  const timeRows: { [key: string]: { [key: string]: (Event | CalendarEvent)[][] } } = {}
  const totalRowsByCategory: { [key: string]: number } = {}
  for (const categoryId in eventsByCategory) {
    const eventsInCategory = eventsByCategory[categoryId]
    timeRows[categoryId] = {}

    eventsInCategory.forEach((event) => {
      const typeId = event.type
      if (!timeRows[categoryId][typeId]) {
        timeRows[categoryId][typeId] = []
      }

      const newRow = [event]
      let addedToRow = false

      for (const row of timeRows[categoryId][typeId]) {
        if (!addedToRow && !row.some((rowEvent) => checkOverlap(rowEvent, event))) {
          row.push(event)
          addedToRow = true
        }
      }

      if (!addedToRow) {
        timeRows[categoryId][typeId].push(newRow)
      }
    })

    totalRowsByCategory[categoryId] = Object.values(timeRows[categoryId]).reduce(
      (total, rowsOfType) => total + rowsOfType.length,
      0
    )
  }

  for (const categoryId in timeRows) {
    const typesInCategory = timeRows[categoryId]
    for (const typeId in typesInCategory) {
      const rowsOfType = typesInCategory[typeId]
      const staggeredRows: (Event | CalendarEvent)[][] = []

      rowsOfType.forEach((row) => {
        let addedToRow = false
        for (const staggeredRow of staggeredRows) {
          if (
            !addedToRow &&
            !staggeredRow.some((staggeredEvent) => row.some((rowEvent) => checkOverlap(rowEvent, staggeredEvent)))
          ) {
            staggeredRow.push(...row)
            addedToRow = true
          }
        }
        if (!addedToRow) {
          staggeredRows.push([...row.map((event) => ({ ...event }))]) // Create a new array for the non-overlapping set
        }
      })

      timeRows[categoryId][typeId] = staggeredRows
    }
  }

  function checkOverlap(eventA: Event | CalendarEvent, eventB: Event | CalendarEvent): boolean {
    if (!eventA.startTime || !eventB.startTime) {
      return false
    }

    const startTimeA = new Date(eventA.startTime).getTime()
    const endTimeA = eventA.endTime ? new Date(eventA.endTime).getTime() : null
    const startTimeB = new Date(eventB.startTime).getTime()
    const endTimeB = eventB.endTime ? new Date(eventB.endTime).getTime() : null

    return (endTimeB == null || startTimeA <= endTimeB) && (endTimeA == null || startTimeB <= endTimeA)
  }

  return { timeRows, totalRowsByCategory }
}

function isCalendarEvent(obj: unknown): obj is CalendarEvent {
  return !!obj && typeof obj === 'object' && 'attribute' in obj
}

function getHighestPriorityInTypeRows(typeRows: (Event | CalendarEvent)[][]): number {
  let highestPriority = Number.NEGATIVE_INFINITY

  typeRows.forEach((row) => {
    row.forEach((event) => {
      if ('priority' in event && typeof event.priority === 'number') {
        highestPriority = Math.max(highestPriority, event.priority)
      }
    })
  })

  return highestPriority
}

function getTypeKeysSortedByPriority(
  categoryTimeRows: { [key: string]: (Event | CalendarEvent)[][] },
  isCollectedSettings: boolean
): string[] {
  const typeKeys = Object.keys(categoryTimeRows)
  if (!isCollectedSettings) {
    return typeKeys
  }

  return typeKeys
    .map((typeKey, index) => ({
      typeKey,
      index,
      highestPriority: getHighestPriorityInTypeRows(categoryTimeRows[typeKey]),
    }))
    .sort((a, b) => {
      if (a.highestPriority !== b.highestPriority) {
        return b.highestPriority - a.highestPriority
      }

      return a.index - b.index
    })
    .map(({ typeKey }) => typeKey)
}

export default function BarCalendar({
  startTime: periodStartTime,
  endTime: periodEndTime,
  events,
  categories,
  onClick,
  isEventHighlighted,
  disableDefaultSorting,
  header = i18n.t('Unit'),
  categoryColumnWidth = 240,
  isOperationalEventCalendar = false,
  isCollectedSettings = false,
}: BarCalenderProps): ReactElement {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  useCalendarScrollPosition(scrollContainerRef, categoryColumnWidth)

  const startTime = Datetime.toISOString(new Date(new Date(periodStartTime).setHours(0, 0, 0, 0)))
  const endTime = Datetime.toISOString(new Date(new Date(periodEndTime).setHours(23, 59, 59, 999)))

  const currentDate: ISODateTime = Datetime.getISONow(0)

  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) => a.name.localeCompare(b.name))
  }, [categories])

  const dates = useMemo(() => {
    return Datetime.getDatesBetween(startTime, endTime)
  }, [startTime, endTime])

  const sortedEvents = useMemo(() => {
    if (disableDefaultSorting) {
      return events
    }

    return [...events].sort((a, b) => {
      const aStartTimeMs = a.startTime ? new Date(a.startTime).getTime() : 0
      const bStartTimeMs = b.startTime ? new Date(b.startTime).getTime() : 0
      return aStartTimeMs - bStartTimeMs
    })
  }, [events, disableDefaultSorting])

  const eventColors = [colors.primary3]
  const typeColors: { [key: string]: string } = {}
  sortedEvents.forEach((event) => {
    if (!typeColors[event.type]) {
      typeColors[event.type] = eventColors[0]
      if (isCalendarEvent(event) && event.attribute) {
        if (event.attribute.includes('max') || event.attribute.includes('min')) {
          // yellow
          typeColors[event.type] = colors.warning4
        } else if (event.attribute.includes('availability') || event.attribute.includes('forced')) {
          // red
          typeColors[event.type] = colors.danger3
        }
      }
    }
  })
  sortedEvents.forEach((event) => {
    if (isOperationalEventCalendar) {
      return
    }

    if (isCollectedSettings && 'priority' in event && event.priority !== undefined) {
      const barColors = getPriorityBarColors(event.priority, event.priorityColor)
      event.color = barColors.background
      event.textColor = barColors.text
    } else {
      event.color = typeColors[event.type]
    }
  })

  // Format events into rows
  const { timeRows, totalRowsByCategory } = useMemo(() => {
    return formatEvents(sortedEvents, startTime, endTime)
  }, [sortedEvents, startTime, endTime])

  const defaultRowHeight = 35
  const defaultDayWidth = `(100cqi - var(--bar-calendar-category-column-width))/${Math.min(dates.length, 8)}`

  // Calculate the number of hours between the min and max date
  const visibleHours = useMemo(() => {
    const minTimestamp = new Date(startTime).getTime()
    const maxTimestamp = new Date(endTime).getTime()
    const hours = Math.round((maxTimestamp - minTimestamp) / 3600e3)
    return hours
  }, [startTime, endTime])

  const highlightedCategoryIds = new Set(
    isEventHighlighted ? sortedEvents.filter(isEventHighlighted).map((event) => event.categoryId) : []
  )

  const scrollContainerStyle: CSSProperties & Record<'--bar-calendar-category-column-width', string> = {
    overflowX: dates.length <= 8 ? 'hidden' : 'auto',
    '--bar-calendar-category-column-width': `${categoryColumnWidth}px`,
  }

  return (
    // e2e: bar-calendar is the Robot/Playwright root scope selector — do not remove
    <div className={styles.SettingsCalendar} data-testid="bar-calendar">
      <div
        ref={scrollContainerRef}
        className={styles.ScrollContainer}
        style={scrollContainerStyle}
        data-testid="bar-calendar-scroll-container"
      >
        <div className={styles.ScrollContent}>
          <div className={styles.CategoryColumn}>
            <div className={styles.StickyHeader}>
              <Row>
                <Column header={{ textContent: header }} />
              </Row>
            </div>
            {sortedCategories.map((category: Category) => {
              const nRows = totalRowsByCategory[category.id] || 0
              return nRows > 0 ? (
                <div key={category.id}>
                  <Row
                    height={defaultRowHeight * nRows}
                    centerContent={true}
                    className={highlightedCategoryIds.has(category.id) ? styles.HighlightedCategoryName : undefined}
                  >
                    <BCCategoryName name={category.name} />
                  </Row>
                </div>
              ) : null
            })}
          </div>
          <div className={styles.CalenderSection}>
            <div className={styles.StickyHeader}>
              <Row height={defaultRowHeight}>
                {dates.map((date: ISODateTime) => {
                  const isToday: boolean =
                    Datetime.toLocalTime(date, 'onlyDate') === Datetime.toLocalTime(currentDate, 'onlyDate')
                  const isWeekend: boolean = Datetime.isWeekend(date)
                  const textContent = Datetime.toLocalTime(date, 'shortDayTextWithMonth')
                  return <Column header={{ textContent, isToday, isWeekend }} width={defaultDayWidth} key={date} />
                })}
              </Row>
            </div>
            {sortedCategories.map((category: Category) => {
              if (!totalRowsByCategory[category.id]) return
              if (!timeRows[category.id]) {
                return (
                  <Row key={category.id} height={defaultRowHeight} width={`calc(${defaultDayWidth}*${dates.length})`} />
                )
              }

              const categoryTimeRows = timeRows[category.id]
              const typeKeys = getTypeKeysSortedByPriority(categoryTimeRows, isCollectedSettings)

              return (
                <div
                  key={category.id}
                  style={{
                    width: `calc(${defaultDayWidth}*${dates.length})`,
                  }}
                >
                  {typeKeys.map((typeKey) => {
                    const typeTimeRows = categoryTimeRows[typeKey]
                    return (
                      <React.Fragment key={typeKey}>
                        {typeTimeRows.map((row: (Event | CalendarEvent)[], rowIndex: number) => {
                          const visibleEvents = getVisibleEvents(row, startTime, endTime)
                          return (
                            <Row
                              key={`${typeKey}-${rowIndex}`}
                              height={defaultRowHeight}
                              width={`calc(${defaultDayWidth}*${dates.length})`}
                              padded={true}
                              className={
                                highlightedCategoryIds.has(category.id) ? styles.HighlightedCategoryRow : undefined
                              }
                            >
                              {visibleEvents.map((event: Event, eventIndex: number) => {
                                const widthRatio = event.hours ? event.hours / visibleHours : 1
                                return (
                                  <EventBar
                                    event={{
                                      ...event,
                                      categoryName: category.name,
                                    }}
                                    onClick={onClick}
                                    highlighted={event.id !== FILLER_EVENT_ID && !!isEventHighlighted?.(event)}
                                    width={`calc(${defaultDayWidth}*${dates.length * widthRatio})`}
                                    // Stable id-based key so React reconciles real bars in place
                                    // across Pusher refetches (no unmount/remount) — avoids the
                                    // "element detached" e2e flake. Fillers can repeat, so they
                                    // keep an index-based key.
                                    key={
                                      event.id === FILLER_EVENT_ID
                                        ? `${FILLER_EVENT_ID}-${typeKey}-${rowIndex}-${eventIndex}`
                                        : `${event.id}`
                                    }
                                  />
                                )
                              })}
                            </Row>
                          )
                        })}
                      </React.Fragment>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
